import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import webAuthnService from '../../../services/webAuthnService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const FaceIDForm = ({ onSubmit, loading }) => {
  const { signIn } = useAuth();
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [registrationStep, setRegistrationStep] = useState('credentials'); // 'credentials' or 'biometric'
  const [error, setError] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState(''); // Store actual email after validation

  useEffect(() => {
    const checkSupport = async () => {
      const supported = webAuthnService?.isSupported();
      setIsSupported(supported);
      
      if (supported) {
        const hasStoredCredentials = await webAuthnService?.hasCredentials();
        setHasCredentials(hasStoredCredentials);
      }
    };
    
    checkSupport();
  }, []);

  const handleFaceIDAuthentication = async () => {
    setIsScanning(true);
    setScanResult(null);
    setError('');

    try {
      // Authenticate using WebAuthn and restore session
      const authResult = await webAuthnService?.authenticate();

      if (!authResult?.success) {
        throw new Error(authResult.error);
      }

      setScanResult('success');
      
      // Session is already restored by webAuthnService
      // Just notify parent that authentication is complete
      setTimeout(() => {
        onSubmit({ 
          method: 'faceid', 
          authenticated: true,
          email: authResult?.email 
        });
      }, 1000);
    } catch (error) {
      console.error('Face ID authentication failed:', error);
      setError(error?.message || 'Authentication failed');
      setScanResult('error');
    } finally {
      setIsScanning(false);
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (registrationStep === 'credentials') {
      // Validate credentials with Supabase WITHOUT creating a session
      try {
        // Check if identifier is email or username
        const emailRegex = /\S+@\S+\.\S+/;
        const isEmail = emailRegex?.test(identifier);
        let loginEmail = identifier;

        // If not email format, it's a username - use secure function to get email
        if (!isEmail) {
          const { data: emailData, error: lookupError } = await supabase?.rpc('get_email_from_username', { p_username: identifier });

          if (lookupError || !emailData) {
            setError('Invalid username or password');
            return;
          }

          loginEmail = emailData;
        }

        // Validate credentials WITHOUT creating a session (use signInWithPassword then immediately sign out)
        const { data, error: authError } = await supabase?.auth?.signInWithPassword({
          email: loginEmail,
          password: password,
        });
        
        if (authError) {
          setError(authError?.message || 'Invalid credentials');
          return;
        }

        if (data?.user && data?.session) {
          // Store the actual email and session info for biometric registration
          setRegisteredEmail(loginEmail);
          
          // Move to biometric registration step WITHOUT keeping the session active
          // We'll use the refresh token from this session for biometric storage
          // but we need to keep the session active temporarily for registration
          setRegistrationStep('biometric');
        }
      } catch (err) {
        setError('Authentication failed. Please check your credentials.');
      }
    } else if (registrationStep === 'biometric') {
      // Register WebAuthn credential
      setIsScanning(true);
      
      try {
        // Get current session
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        
        if (sessionError || !session) {
          throw new Error('No active session found. Please try again.');
        }

        const result = await webAuthnService?.registerCredential(
          session?.user?.id,
          registeredEmail, // Use the validated email
          session?.refresh_token
        );

        if (!result?.success) {
          throw new Error(result.error);
        }

        setScanResult('success');
        setHasCredentials(true);
        
        // Sign out after successful registration to prevent auto-login
        await supabase?.auth?.signOut();
        
        // Complete registration and return to login screen
        setTimeout(() => {
          setShowRegistration(false);
          setRegistrationStep('credentials');
          setIdentifier('');
          setPassword('');
          setRegisteredEmail('');
          setScanResult(null);
          setError('');
        }, 2000);
      } catch (error) {
        console.error('Biometric registration failed:', error);
        setError(error?.message || 'Failed to register biometric authentication');
        setScanResult('error');
      } finally {
        setIsScanning(false);
      }
    }
  };

  const getScanStatusContent = () => {
    if (isScanning) {
      return {
        icon: 'Scan',
        title: 'Scanning...',
        message: registrationStep === 'biometric' ?'Please authenticate with your biometric sensor' :'Please look at your camera',
        color: 'text-primary'
      };
    }
    
    if (scanResult === 'success') {
      return {
        icon: 'CheckCircle',
        title: registrationStep === 'biometric' ? 'Registration Successful' : 'Authentication Successful',
        message: registrationStep === 'biometric' ?'Face ID has been enabled for your account' :'Redirecting to dashboard...',
        color: 'text-success'
      };
    }
    
    if (scanResult === 'error') {
      return {
        icon: 'XCircle',
        title: 'Authentication Failed',
        message: error || 'Please try again or use another method',
        color: 'text-error'
      };
    }
    
    return {
      icon: 'Scan',
      title: 'Face ID Authentication',
      message: 'Tap to authenticate with Face ID',
      color: 'text-muted-foreground'
    };
  };

  const statusContent = getScanStatusContent();

  if (!isSupported) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="AlertTriangle" size={24} className="text-warning" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Face ID Not Supported</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Your device or browser doesn't support biometric authentication.
          Please use another authentication method.
        </p>
      </div>
    );
  }

  // Registration flow
  if (showRegistration) {
    if (registrationStep === 'biometric') {
      return (
        <div className="text-center py-8">
          <div className={`
            w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 transition-smooth
            ${isScanning ? 'bg-primary/20 animate-pulse' : 
              scanResult === 'success' ? 'bg-success/20' :
              scanResult === 'error' ? 'bg-error/20' : 'bg-muted'}
          `}>
            <Icon 
              name={statusContent?.icon} 
              size={32} 
              className={`${statusContent?.color} transition-smooth`}
            />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {statusContent?.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-8">
            {statusContent?.message}
          </p>
          
          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {!isScanning && scanResult !== 'success' && (
            <div className="space-y-4">
              <Button
                onClick={handleRegistrationSubmit}
                variant="default"
                size="lg"
                fullWidth
                disabled={isScanning}
                iconName="Scan"
                iconPosition="left"
              >
                {scanResult === 'error' ? 'Try Again' : 'Register Biometric'}
              </Button>
              
              <Button
                onClick={() => {
                  setShowRegistration(false);
                  setRegistrationStep('credentials');
                  setError('');
                  setScanResult(null);
                }}
                variant="ghost"
                size="lg"
                fullWidth
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <form onSubmit={handleRegistrationSubmit} className="space-y-4">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="UserPlus" size={24} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Register Face ID
          </h3>
          <p className="text-sm text-muted-foreground">
            Enter your credentials to enable biometric authentication
          </p>
        </div>
        {error && (
          <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}
        <Input
          type="text"
          placeholder="Email or Username"
          value={identifier}
          onChange={(e) => setIdentifier(e?.target?.value)}
          required
          iconName="Mail"
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e?.target?.value)}
          required
          iconName="Lock"
        />
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue
        </Button>
        <Button
          type="button"
          onClick={() => {
            setShowRegistration(false);
            setError('');
            setIdentifier('');
            setPassword('');
            setRegisteredEmail('');
          }}
          variant="ghost"
          size="lg"
          fullWidth
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Back to Login
        </Button>
      </form>
    );
  }

  // Login flow
  if (!hasCredentials) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Scan" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Face ID Not Set Up
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          You haven't registered biometric authentication yet.
          Set it up to enable quick and secure login.
        </p>
        <Button
          onClick={() => setShowRegistration(true)}
          variant="default"
          size="lg"
          fullWidth
          iconName="UserPlus"
          iconPosition="left"
        >
          Register Face ID
        </Button>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <div className={`
        w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 transition-smooth
        ${isScanning ? 'bg-primary/20 animate-pulse' : 
          scanResult === 'success' ? 'bg-success/20' :
          scanResult === 'error' ? 'bg-error/20' : 'bg-muted'}
      `}>
        <Icon 
          name={statusContent?.icon} 
          size={32} 
          className={`${statusContent?.color} transition-smooth`}
        />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {statusContent?.title}
      </h3>
      <p className="text-sm text-muted-foreground mb-8">
        {statusContent?.message}
      </p>
      
      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {!isScanning && scanResult !== 'success' && (
        <div className="space-y-4">
          <Button
            onClick={handleFaceIDAuthentication}
            variant="default"
            size="lg"
            fullWidth
            loading={loading}
            disabled={isScanning}
            iconName="Scan"
            iconPosition="left"
          >
            {scanResult === 'error' ? 'Try Again' : 'Start Face ID Scan'}
          </Button>
        </div>
      )}

      {/* Security Notice */}
      <div className="mt-8 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-primary mt-0.5" />
          <div className="text-left">
            <p className="text-xs font-medium text-foreground mb-1">Secure Authentication</p>
            <p className="text-xs text-muted-foreground">
              Your biometric data is processed locally and never stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaceIDForm;